<template>

  <wgu-module-card
    v-bind="$attrs"
    moduleName="ids-filter-module"
    ref="ids-filter-module-win"
    class="mx-auto ids-filter-panel"
    :icon="icon"
    :width="$vuetify.breakpoint.xs ? 300 : 500"
    max-width="600"
  >

    <v-container class="ids-filter-container">

      <!-- location type chooser - e.g. Aufnahmeort -->
      <ids-location-type-chooser
        :locationTypeList="locationTypeList"
        :locationType="locationType"
        @ids-location-type-change="(newLT) => { this.locationType = newLT }"
      />

      <!-- tab bar -->
      <v-tabs
        v-model="tab"
        color="primary"
        class="pl-2 mb-2"
        slider-color="black"
        active-class="ids-active-filter-type-tab"
      >
        <v-tab
          v-for="tabItem in tabItems"
          :key="tabItem.tab"
        >
          {{ tabItem.tab }}
        </v-tab>
      </v-tabs>


      <!-- tab content -->
      <v-tabs-items v-model="tab">
        <v-tab-item
          v-for="tabItem in tabItems"
            :key="tabItem.tab"
        >

          <ids-corpora-filter
            v-if="tabItem.type === 'corpora'"
            :corpusListApi="corpusList"
            :corpusSelection="corpusSelection"
            @ids-corpus-selection-change="newSel => corpusSelection=newSel"
          />

          <ids-geographic-filter
            v-else-if="tabItem.type === 'geo'"
            :countries="countryList"
            :countrySelection="countrySelection"
            :regionSelection="regionSelection"
            :districtSelection="districtSelection"
            :locationType="locationType"
            @ids-geog-filter-change="onGeographicFilterChange"
          />

        </v-tab-item>
      </v-tabs-items>

    </v-container>

    <v-toolbar
      color="primary"
      dark
      dense
    >
      <v-btn
        @click="this.requestLocations"
      >
        ANWENDEN
      </v-btn>
    </v-toolbar>

    <!-- error message -->
    <v-dialog v-model="apiError" >
      <v-container fluid fill-height class="ids-error-msg-ct">
        <v-layout justify-center align-center>
          <div>
          <h3>Fehler</h3>
          <p>Die Ortsdaten konnten nicht vom Server geladen werden.</p>
          </div>
        </v-layout>
      </v-container>
    </v-dialog>
    <!-- END error message -->

    <!-- dev only to show API call, TODO remove -->
    <v-snackbar
      top
      absolute
      v-model="snackbar"
      :timeout="snackbarTimeout"
      class="ids-dev-api-snackbar"
    >
      <div>
        API Call:
        <br>
        <span><code>{{ dummyApiCall }}</code></span>
      </div>
    </v-snackbar>
    <!-- END: dev only to show API call, TODO remove -->

  </wgu-module-card>

</template>

<script>
import Vue from 'vue';
import ModuleCard from '../../../../src/components/modulecore/ModuleCard';
import { Mapable } from '../../../../src/mixins/Mapable'
import LocationTypeChooser from './IdsLocationTypeChooser.vue';
import CorporaFilter from './IdsCorporaFilter.vue';
import GeographicFilter from './IdsGeographicFilter.vue';

import DgdDataLoader from '../../DgdDataLoader';
import IdsFilterGenerator from '../../IdsFilterGenerator.js';
import IdsCorpusLookup from '../../IdsCorpusLookup.js';
import { WguEventBus } from '../../../../src/WguEventBus';

export default {
  name: 'ids-filter-module-win',
  components: {
    'wgu-module-card': ModuleCard,
    'ids-location-type-chooser': LocationTypeChooser,
    'ids-corpora-filter': CorporaFilter,
    'ids-geographic-filter': GeographicFilter
  },
  mixins: [Mapable],
  props: {
    icon: { type: String, required: false, default: 'filter' },
    showFilterOnStartup: { type: Boolean, required: false, default: true },
    closeFilterAfterApply: { type: Boolean, required: false, default: false }
  },
  mounted () {
    // load all corpus
    this.loadData();
  },
  data () {
    return {
      locationTypeList: null,
      corpusList: null,
      countryList: null,

      tab: null,
      tabItems: [
        { tab: 'Korpora', type: 'corpora' },
        { tab: 'Geographisch', type: 'geo' }
      ],

      // raw filter selections
      locationType: 'Aufnahmeort',
      corpusSelection: null,

      countrySelection: null, // { iso: 'DE', name: 'Deutschland' },
      regionSelection: null, // { name: 'Rheinland-Pfalz' }
      districtSelection: null, // { name: 'Rhein-Pfalz-Kreis' }
      // END raw filter selections

      apiError: false,

      // dev only, TODO remove
      dummyApiCall: '',
      snackbar: false,
      snackbarTimeout: 3500
      // END dev only
    }
  },

  watch: {
    async locationType () {
      // firing event to inform IdsCorpusCategory that the location type changed
      // CM - 20230217: normally doable by data propagation, but did not work
      WguEventBus.$emit('ids-locationtype-change');
    }
  },

  methods: {
    /**
     * Handler for 'ids-geog-filter-change' event of <ids-geographic-filter>
     */
    onGeographicFilterChange (type, value) {
      if (type === 'country') {
        this.countrySelection = value;
      } else if (type === 'region') {
        this.regionSelection = value;
      } else if (type === 'district') {
        this.districtSelection = value;
      }
    },
    /**
     * Loads data for underlying filter UIs.
     */
    async loadData () {
      // check if we have to wait for the corpus list to be loaded
      if (IdsCorpusLookup.corpusList.length === 0) {
        WguEventBus.$on('ids-corpus-list-load', () => {
          this.corpusList = IdsCorpusLookup.corpusList;
          this.loadData();
        });
        return; // exit here since corpus list is not loaded yet
      } else {
        this.corpusList = IdsCorpusLookup.corpusList;
      }

      // load all location types
      this.locationTypeList = await DgdDataLoader.getLocationTypeList();
      // derive corpus list and its selection
      this.corpusList = IdsCorpusLookup.corpusList;

      const initCorpusIds = IdsCorpusLookup.initialCorpusIds;
      this.corpusSelection = IdsCorpusLookup.getSelectionObjectByArr(initCorpusIds);

      // load all countries
      this.countryList = await DgdDataLoader.getCountryList(this.locationType);

      // signal to WguApp that all filter related base data is loaded
      WguEventBus.$emit('ids-filter-base-data-load');

      // show this filter module window, if configured in app.json
      if (this.showFilterOnStartup) {
        WguEventBus.$emit('ids-filter-module-visibility-change', true)
      }
    },
    /**
     * Creates the current filter and applies it to the request.
     */
    async requestLocations () {
      let geoJsonData;

      // show loading mask
      WguEventBus.$emit('app-loading-mask-toggle', true);

      // inform that we plan to load new locations by filter
      WguEventBus.$emit('ids-before-new-locations-load');

      if (this.tab === 0) {
        // CORPUS FILTER
        if (Vue.prototype.$devMode !== true) {
          // make API call and apply data to map => load 'Ortsdaten'as GeoJSON
          geoJsonData = await DgdDataLoader.getLocationList(this.corpusSelection, this.locationType);
        } else {
          // corpora filter
          const filter = IdsFilterGenerator.createFilter(this.locationType, this.corpusSelection);
          this.dummyApiCall = `get_location_list?${filter}`;
          this.snackbar = true;
        }
      } else if (this.tab === 1) {
        // GEOGRAPHIC FILTER
        const geoFilter = {
          countrySelection: this.countrySelection,
          regionSelection: this.regionSelection,
          districtSelection: this.districtSelection
        };

        if (Vue.prototype.$devMode !== true) {
          // make API call and apply data to map => load 'Ortsdaten'as GeoJSON
          geoJsonData = await DgdDataLoader.getLocationListGeo(geoFilter, this.locationType);
        } else {
          // geographic filter
          const filter = IdsFilterGenerator.createFilter(
            this.locationType,
            null, // corpora filter has to be unset to get geographic filter
            geoFilter
          );
          this.dummyApiCall = `get_location_list_geographic?${filter}`;
          this.snackbar = true;
        }
      }

      if (geoJsonData) {
        WguEventBus.$emit('ids-new-locations-load', geoJsonData);
      } else {
        // show a message to user that data could not be loaded
        this.apiError = true;
      }

      // hide filter module window
      if (this.closeFilterAfterApply && geoJsonData && Vue.prototype.$devMode !== true) {
        this.$refs['ids-filter-module-win'].toggleUi();
      }

      // hide loading mask
      WguEventBus.$emit('app-loading-mask-toggle', false);
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>

  .ids-filter-panel {
    left: auto !important;
    right: 10px !important;
    top: 72px !important;
    z-index: 4; /* ensure module tbar is over app footer*/
  }

  .ids-filter-container {
    height: calc(100vh - 270px);
    padding: 0;
    overflow: scroll;
  }

  .ids-error-msg-ct {
    background-color: beige;
  }

  .v-tab.ids-active-filter-type-tab {
    color: black;
  }

  /* vuetify breakpoint xs */
  @media only screen and (max-width: 600px) {
    .ids-filter-panel {
      left: calc(50% - 150px) !important;
      top: 60px !important;
    }
  }
  .ids-dev-api-snackbar {
    z-index: 1000;
  }

</style>



// WEBPACK FOOTER //
// app/ids/components/filter/IdsFilterModuleWin.vue